<template>
  <div class="bg-card bg-white border-card-border md:px-6">
    <div class="w-full mt-5 md:mt-3">
      <div>
        <h1 slot="header" class="text-xl font-semibold pb-2">Tell us about your loan</h1>
        <h3 slot="header" class="pb-3 border-b mb-4"></h3>
      </div>
      <img
        height="1"
        width="1"
        style="border-style: none"
        alt=""
        src="https://insight.adsrvr.org/track/pxl/?adv=udguyav&ct=0:mvtd0zb&fmt=3"
      />
      <h1 class="font-semibold pb-4">First, select the state in which you reside.</h1>
      <div class="relative w-full mb-4">
        <div class="flex justify-end absolute z-20 md:w-1/3 w-full">
          <i class="fas fa-search font-gray-400 pr-3" style="padding-top: 10px"></i>
        </div>
        <div v-click-outside="clickedOutOfState">
          <Input
            underlined="true"
            loanType="text"
            v-model="applicationInformation.stateOfResidence.value"
            :name="applicationInformation.stateOfResidence.name"
            :placeholder="applicationInformation.stateOfResidence.placeholder"
            :error="applicationInformation.stateOfResidence.error"
            bg="white"
            class="z-30 my-2 md:w-1/3 w-full"
          />
          <div
            v-if="applicationInformation.stateOfResidence.value && !validState"
            class="absolute flex flex-col shadow border rounded inset-x-0 top-0 absolute text-left focus:outline-none md:w-1/3 w-full"
            style="margin-top: 38px"
          >
            <button
              v-for="item in possibleStates"
              aria-label="select"
              v-bind:key="item"
              class="z-40 block bg-white px-4 py-2 w-full text-left font-semibold text-gray-700 focus:outline-none"
              @click="dropdownClick(item)"
            >
              {{ item }}
            </button>
            <div
              v-if="!possibleStates.length"
              class="z-30 bg-white block px-4 py-2 w-full text-left font-semibold text-gray-700 focus:outline-none"
            >
              State not found 🔎
            </div>
          </div>
          <p
            v-if="applicationInformation.stateOfResidence.error.length === 0"
            class="pl-1 pb-2 text-sm text-gray-500"
          >
            The state in which you reside.
          </p>
        </div>
      </div>

      <div class="pt-1" v-if="validState">
        <h1 :class="`font-semibold ${typeError ? 'pb-1' : 'pb-3'}`">
          Enter the loan amount you wish to borrow.
        </h1>
        <transition name="in--down">
          <div
            v-if="typeError.length > 0"
            :class="`pin-r -translate-y-50 bg-white
                                    text-red-400 text-xs rounded font-semibold z-50 pb-2`"
          >
            {{ typeError }}
          </div>
        </transition>
        <div class="py-3">
          <Input
            v-model="applicationInformation.amountAppliedFor.value"
            :error="applicationInformation.amountAppliedFor.error"
            :id="applicationInformation.amountAppliedFor.id"
            :getter="$getters.salary"
            maxlength="6"
            type="text"
            name="Loan Amount"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            :placeholder="'4,500'"
            prefix="$"
            :setter="$setters.salary"
            underlined="true"
            :p="`${`Enter an amount between ${formattedStateMin} and $10,000`}`"
            class="xl:w-2/3 lg:w-2/3 w-full"
          />
        </div>
      </div>
    </div>
    <div class="w-full md:p-4">
      <h1 :class="`font-semibold ${reasonError ? 'pb-1' : 'pb-3'}`">
        What will this loan be used for?
      </h1>
      <transition name="in--down">
        <div
          v-if="reasonError.length > 0"
          :class="`pin-r -translate-y-50 bg-white
                                text-red-400 text-xs rounded font-semibold z-50 pb-2`"
        >
          {{ reasonError }}
        </div>
      </transition>
      <div class="flex flex-wrap">
        <div
          v-for="r in loanReasonOptions"
          v-bind:key="r.word"
          class="w-1/2 md:w-1/3 lg:w-1/4 p-1 flex"
        >
          <button
            type="btn"
            aria-label="loanReason"
            class="w-full h-full py-2 focus:outline-none rounded hover:shadow-md"
            :class="{ 'shadow-md border': loanReason === r.word }"
            @click="applicationInformation.loanReason.value = r.word"
          >
            <i
              :class="`text-xl  md:my-2 ${r.icon} ${
                loanReason === r.word ? 'viva-green-text' : 'text-gray-400'
              }`"
            ></i>
            <p
              class="leading-tight"
              :class="loanReason === r.word ? 'viva-green-text' : 'text-gray-600'"
            >
              {{ r.word }}
            </p>
          </button>
        </div>
        <div v-if="loanReason === 'Other'" class="w-full">
          <Input
            v-model="applicationInformation.other.value"
            :error="applicationInformation.other.error"
            :id="applicationInformation.other.id"
            type="text"
            name="Brief Description"
            placeholder="Paying off credit card"
            p="Please provide a brief explanation of why you need a loan"
            class="my-3 w-full"
            underlined="true"
          />
        </div>
      </div>
    </div>
    <div class="flex w-full mt-4">
      <button
        class="hover:text-primary mr-auto focus:outline-none rounded"
        type="btn"
        aria-label="navigate back"
        v-on:click="handleBack()"
      >
        <i
          class="fas fa-arrow-left bg-gray-300 rounded-full flex items-center text-lg justify-center w-8 h-8"
        ></i>
      </button>

      <base-button
        type="primary ml-auto"
        :disabled="!nextEnabled"
        aria-label="navigate next"
        @click="handleNext()"
      >
        Next
      </base-button>
    </div>
    <transition name="in--up">
      <div v-if="!nextEnabled" class="flex items-center justify-center w-full px-4">
        <h3 class="text-red-500 text-sm">Please fix all errors before proceeding.</h3>
      </div>
    </transition>
  </div>
</template>

<script>
import Engine from '@/mixins/Engine'
import stateNamesAndAbreviations from '@/assets/stateNamesAndAbreviations.json'

export default {
  name: 'LoanDetails',
  mixins: [Engine],
  // props: {
  //   initialData: Object
  // },

  data: function () {
    return {
      applicationInformation: {
        amountAppliedFor: {
          name: 'Loan Amount',
          value: '',
          error: '',
          id: 'loan_amount'
        },
        other: {
          name: 'Other',
          optional: true,
          value: '',
          error: '',
          id: 'other_reason'
        },
        loanReason: {
          name: 'Reason',
          value: '',
          error: ''
        },
        stateOfResidence: {
          name: 'State of Residence',
          placeholder: 'Georgia',
          value: '',
          error: ''
        },
        loanType: {
          name: 'loanType',
          value: 'lifestyle',
          error: ''
        }
      },
      loanReasonOptions: [
        { icon: 'fas fa-car', word: 'Auto Purchase or Repair' },
        { icon: 'fas fa-credit-card', word: 'Credit Repair' },
        { icon: 'fas fa-piggy-bank', word: 'Debt Consolidation' },
        { icon: 'fas fa-graduation-cap', word: 'Education Expenses' },
        { icon: 'fas fa-hourglass', word: 'Emergency Expense' },
        { icon: 'fas fa-paint-roller', word: 'Home Improvements' },
        { icon: 'fas fa-unlock-alt', word: 'Insurance Deductible' },
        { icon: 'fas fa-shopping-cart', word: 'Large Purchase' },
        { icon: 'fas fa-balance-scale', word: 'Legal Costs' },
        { icon: 'fas fa-stethoscope', word: 'Medical Bills' },
        { icon: 'fas fa-money-bill-wave', word: 'Pay Bills' },
        { icon: 'fas fa-home', word: 'Rent Expense' },
        { icon: 'fas fa-hammer', word: 'Repairs' },
        { icon: 'fas fa-plane-departure', word: 'Vacation' },
        { icon: 'fas fa-glass-cheers', word: 'A Wedding' },
        { icon: 'fas fa-ellipsis-h', word: 'Other' }
      ],
      stateNamesAndAbreviations,
      firstClick: true
    }
  },
  methods: {
    handleBack() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      this.$emit('back')
    },
    handleNext() {
      if (this.firstClick) {
        this.firstClick = false
        const emptyRequired = Object.keys(this.applicationInformation).filter(
          (fieldName) =>
            !this.applicationInformation[fieldName].optional &&
            this.applicationInformation[fieldName].value.length === 0
        )
        const existingErrors = Object.keys(this.applicationInformation).filter(
          (fieldName) => this.applicationInformation[fieldName].error.length > 0
        )
        if (emptyRequired.length + existingErrors.length > 0) {
          emptyRequired.forEach((requiredField) => {
            this.applicationInformation[requiredField].error = 'This is a required field.'
          })
          return
        }
        if (this.applicationInformation.amountAppliedFor.error.length > 0) {
          return
        }
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
      const outputData = {}
      // outputData.stateOfResidence = {
      //   name: this.properNoun(this.stateUpper),
      //   abbreviation: this.stateAbbr
      // }
      outputData.stateOfResidence = this.stateAbbr
      outputData.loanType = this.loanType
      outputData.amountAppliedFor = Math.round(this.amountAppliedFor)
      if (this.otherReason) {
        outputData.loanReason = 'Other'
        outputData.otherDescription = `${this.otherReason}`
      } else {
        outputData.loanReason = this.loanReason
      }
      this.$emit('updateData', outputData)
      this.$emit('next')
    },
    dropdownClick(v) {
      this.applicationInformation.stateOfResidence.value = v.slice(0, v.length - 5)
    },
    clickedOutOfState() {
      // Basically the watcher for the state dropdown
      console.log(
        'this.applicationInformation.stateOfResidence: ',
        this.applicationInformation.stateOfResidence
      )
      if (!this.validState) {
        this.applicationInformation.stateOfResidence.value = ''
        this.applicationInformation.stateOfResidence.error = 'Please select a state.'
      } else {
        this.applicationInformation.stateOfResidence.error = ''
      }
    }
  },
  computed: {
    // ...mapFields(['stateOfResidence', 'amountAppliedFor', 'loanReason', 'loanType']),
    stateUpper() {
      return this.applicationInformation.stateOfResidence.value.toUpperCase()
    },
    stateAbbr() {
      if (this.validState) {
        return this.states.find((s) => s.name === this.stateUpper).abbr
      }
      return ''
    },
    stateError() {
      return this.applicationInformation.stateOfResidence.error
    },
    states() {
      return this.stateNamesAndAbreviations['US States']
    },
    possibleStates() {
      return this.states
        .filter((s) => s.name.includes(this.stateUpper) || s.abbr.includes(this.stateUpper))
        .map((s) => `${this.properNoun(s.name)} (${s.abbr})`)
    },
    validState() {
      return !!this.states.find((s) => s.name === this.stateUpper)
    },
    stateMin() {
      if (['GA'].includes(this.stateAbbr)) {
        return 3001
      } else {
        return 3000
      }
    },
    formattedStateMin() {
      const currency = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(this.stateMin)

      return currency.split('.')[0]
    },
    loanType() {
      return this.applicationInformation.loanType.value
    },
    typeError() {
      return this.applicationInformation.loanType.error
    },
    loanReason() {
      return this.applicationInformation.loanReason.value
    },
    reasonError() {
      return this.applicationInformation.loanReason.error
    },
    otherReason() {
      return this.applicationInformation.other.value.trim()
    },
    amountAppliedFor() {
      return this.applicationInformation.amountAppliedFor.value
    },
    fieldsValid() {
      const errorsExist = Object.keys(this.applicationInformation).filter(
        (fieldName) => this.applicationInformation[fieldName].error.length > 0
      )
      const emptyRequired = Object.keys(this.applicationInformation).filter(
        (fieldName) =>
          !this.applicationInformation[fieldName].optional &&
          this.applicationInformation[fieldName].value.length === 0
      )
      return errorsExist.length + emptyRequired.length === 0
    },
    nextEnabled() {
      return this.firstClick || this.fieldsValid
    }
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    const initialData = {}
    Object.keys(this.applicationInformation).map((k) => {
      let v = this.$store.state.applicationData[k]
      if (v) initialData[k] = v
    })
    Object.keys(initialData).forEach((k) => {
      if (k === 'stateOfResidence') {
        if (initialData[k].length === 2) {
          this.applicationInformation[k].value = this.stateAbbrToName(initialData[k])
        }
      } else {
        this.applicationInformation[k].value = initialData[k]
      }
    })
  },
  watch: {
    amountAppliedFor: {
      handler(v, ov) {
        if (!isNaN(v)) {
          if (!(v == 0 && ov != 0)) {
            if (v < this.stateMin || v > 10000) {
              this.applicationInformation.amountAppliedFor.error = `Lifestyle Loans must be between ${this.formattedStateMin} and $10,000.  Please change your request loan amount.`
            } else {
              this.applicationInformation.amountAppliedFor.error = ''
            }
          }
        } else {
          this.applicationInformation.amountAppliedFor.error = 'Amount applied for cannot be empty.'
        }
      }
    },
    loanType: {
      handler() {
        if (this.loanType === 'starter' && this.stateMin == 500) {
          this.applicationInformation.amountAppliedFor.value = 500
        }
        if (this.loanType.length > 1) {
          this.applicationInformation.loanType.error = ''
        }
      }
    },
    loanReason: {
      handler() {
        if (this.loanReason.length > 1) {
          this.applicationInformation.loanReason.error = ''
        }
      }
    },
    stateAbbr: {
      handler(v, ov) {
        if (ov != '' && v != ov) {
          this.applicationInformation.amountAppliedFor.value = ''
        }
      }
    }
  }
}
</script>
